//core
import React, {useEffect, useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import { useField, useFormikContext } from "formik";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isValid } from "date-fns";
import {DateTimePicker, DesktopDateTimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {FormControl, FormHelperText, InputAdornment, IconButton, Box} from "@mui/material";

//styles
import { useStyles } from "./styles";
import Calendar from "@mui/icons-material/Event";
import { DateOrTimeView } from "@mui/x-date-pickers/models";

//hooks
import { useLocale } from "../../../../customHooks/useLocale";

//types
interface IMuiFieldProps {
  id?: string;
  name: string;
  autoComplete?: string;
  label: string;
  value?: any;
  secondValue?: any;
  maxDate?: any,
  minDate?: any,
  //type?: React.InputHTMLAttributes<unknown>['type'];
  hideError?: boolean,
  placeholder?: string,
  disabled?: boolean,
  className?: string,
  helperText?: string,
  dataId?: string,
  InputProps?: any,
  error?: boolean | string,
  validation?: (value: any) => any,
  validate?: (value: any) => any,
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  inputFormat?: string;
  views?: DateOrTimeView[];
  onCloseCallback?: () => void;
  closeOnOutside?: boolean;
}

export const MuiSingleDateTimePicker = (props: IMuiFieldProps) => {
  const styles = useStyles();
  const [field, meta] = useField(props);
  const ref: any = useRef<HTMLDivElement>();

  const {  i18n } = useTranslation();
  const locale = useLocale()
  const { setFieldValue, setFieldError } = useFormikContext();

  const {
    id,
    name,
    autoComplete,
    label,
    value,
    secondValue,
    disabled = false,
    maxDate,
    minDate,
    error = '',
    hideError = false,
    //views = [],
    inputFormat = 'dd/MM/yyyy HH:mm',
    views = ["year", "month", "day", "hours", "minutes"],
    onCloseCallback,
    closeOnOutside = false
  } = props;


  const [fieldVal, setFieldVal] = React.useState<Date | null>(new Date(value));
  const [open, setOpen] = useState(false);
  const [hasValidationError, setHasValidationError] = useState<boolean>(false);

  // Validate date function
  const validateDate = (date: Date | null): boolean => {
    if (!date) return true; // null is considered valid (empty field)

    // Check if date is valid
    if (!isValid(date)) {
      return false;
    }

    // Check against min/max dates if provided
    if (minDate && date < new Date(minDate)) {
      return false;
    }

    if (maxDate && date > new Date(maxDate)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!value || value === '' || value === null || value === undefined) {
      setFieldVal(null);
      return;
    }
    const date = new Date(value);
    setFieldVal(isValid(date) ? date : null);
  }, [value]);

  useEffect(() => {
    if (fieldVal !== null) {
      if (name === 'at_end_show') {
        if (new Date(fieldVal) < new Date(secondValue)) {
          setFieldValue(field.name, secondValue);
          setFieldVal(secondValue);
        }
      }
      if (name === 'at_beginning_show') {
        if (new Date(fieldVal) > new Date(secondValue)) {
          setFieldValue('at_end_show', fieldVal);
          setFieldVal(fieldVal);
        }
      }
    }
  }, [fieldVal, minDate]);

  const handleClose = () => {
    setOpen(false);
    if (typeof onCloseCallback === 'function') {
      onCloseCallback();
    }
  };

  const handleDateChange = (newValue: Date | null) => {
    setFieldVal(newValue);

    const isValidDate = validateDate(newValue);
    setHasValidationError(!isValidDate);

    if (isValidDate) {
      // Clear any existing error and set the valid value
      setFieldError(name, undefined);
      setFieldValue(field.name, newValue, true);
    } else {
      // Set error when date is not valid
      let errorMessage = 'Invalid date';

      if (newValue && !isValid(newValue)) {
        errorMessage = 'Invalid date format';
      } else if (minDate && newValue && newValue < new Date(minDate)) {
        errorMessage = 'Date is before minimum allowed date';
      } else if (maxDate && newValue && newValue > new Date(maxDate)) {
        errorMessage = 'Date is after maximum allowed date';
      }

      setFieldError(name, errorMessage);
    }
  };

  return (
    <LocalizationProvider
      //@ts-ignore
      dateAdapter={AdapterDateFns} adapterLocale={locale.locale}>
      <div className={styles.formField}>
        <FormControl fullWidth>
          <DateTimePicker
            views={views}
            ampm={false}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={handleClose}
            label={label}
            disabled={disabled}
            //value={new Date(value)}
            value={fieldVal}
            format={i18n.language === 'en' ? 'MM/dd/yyyy HH:mm' : inputFormat}
            // @ts-ignore
            closeOnSelect={true}
            {...(maxDate ? {maxDate: new Date(maxDate)} : {})}
            {...(minDate ? {minDate: new Date(minDate)} : {})}
            // @ts-ignore
            onChange={handleDateChange}
            slotProps={{
              popper: {
                disablePortal: false,
                ref: ref,
                style: { zIndex: 999999 }
              },
              textField: {
                size: 'small',
                id: id,
                className: `${styles.input} ${ !!(meta.touched && !!meta.error && meta.error !== 'error') ? styles.inputError : styles.inputErrorDef }`,
                InputProps: {
                  endAdornment: <InputAdornment position="end">
                    <IconButton
                      disabled={disabled}
                      size="small"
                      className={styles.iconButton}
                      sx={{ padding: '2px' }}
                      onClick={() => {
                        if (disabled) {return false;}
                        setOpen(!open);
                      }}
                    >
                      <Calendar color={disabled ? 'disabled' : 'action'} />
                    </IconButton>
                  </InputAdornment>,
                },
                autoComplete: autoComplete,
                ...(hideError ? {error: false} : {}),
                ...( !!(meta.touched && meta.error) ? {color: 'error'} : {})
              },
              actionBar: ({ wrapperVariant }) => {
                return {
                  actions: wrapperVariant === 'desktop' ? ['accept'] : ['accept'],
                }
              },
            }}
          />
          { meta.touched && !!meta.error && meta.error !== 'error' ? (
            <FormHelperText error>{meta.error}</FormHelperText>
          ) : null }
        </FormControl>
      </div>
    </LocalizationProvider>
  );
};
